import type { KalturaAgentBlock } from "@/lib/powtoon-be/types";
import type { InteractionMode } from "@/lib/scenarios/interactionModes";

/**
 * The complete Kaltura intellect capability state a scenario asks for.
 *
 * The scenario record is the single source of truth for these values. Kaltura's
 * `capabilities` sub-dict is full-replace, so every write must carry the whole
 * set — that is why the fields here are required booleans, not optional ones.
 */
export type KalturaCapabilityState = {
  web_search: boolean;
  /**
   * Learner screen sharing for vision analysis (Kaltura
   * `screen_share_analysis`). Enables the browser's still-frame push; Kaltura
   * accepts no inbound video track.
   */
  screen_share: boolean;
  /**
   * Trainer allows the avatar to present the scenario's display document via
   * our own browser client tools. Not a Kaltura capability key — it decides
   * whether the presentation tools are minted and attached via `tool_ids`.
   */
  present_document: boolean;
  /** Knowledge record to attach; `null` detaches. */
  knowledge_id: number | null;
};

/**
 * Derive the upstream capability state from a scenario block.
 *
 * Sole owner of the mode gating: web search, pushed media and knowledge attach
 * are conversational-only, and the trainer's stored preference is ignored (not
 * erased) in the structured modes. Every caller that writes to Kaltura —
 * the save pipeline and the session-start reconcile — must go through here so
 * the two can never disagree.
 */
export function kalturaCapabilities(input: {
  block: Partial<KalturaAgentBlock> | null | undefined;
  mode: InteractionMode | string | undefined;
  /**
   * Number of knowledge sources still backed by a live scenario input. The save
   * pipeline recomputes this against the current inputs; the session runtime
   * reads the keys already stored on the block.
   */
  syncedSourceCount?: number;
}): KalturaCapabilityState {
  const { block, mode } = input;
  const isConversational = mode === "conversational";
  const sourceCount =
    input.syncedSourceCount ?? (block?.knowledge_source_keys?.length ?? 0);
  return {
    web_search: isConversational && block?.web_search_enabled === true,
    screen_share: isConversational && block?.screen_share_enabled === true,
    present_document: isConversational && block?.present_document === true,
    knowledge_id:
      isConversational && block?.knowledge_id && sourceCount > 0
        ? (block.knowledge_id as number)
        : null,
  };
}

/**
 * Kaltura capability keys, as they come back on the resolved session, mapped to
 * the state field that owns them. Used to detect out-of-band drift.
 */
const CAPABILITY_KEYS: Array<[string, keyof KalturaCapabilityState]> = [
  ["use_web_search", "web_search"],
  ["use_knowledge_base", "knowledge_id"],
  ["screen_share_analysis", "screen_share"],
  // The SDK's resolved surface reports camelCase (`session.capabilities`),
  // while a raw intellect read reports the snake_case capability keys. Both
  // spellings are listed so drift is caught whichever shape we are handed;
  // keys the source doesn't report are skipped, never treated as a mismatch.
  ["webSearch", "web_search"],
  ["screenShare", "screen_share"],
];

/**
 * Compare the server-resolved capabilities against what the scenario asks for.
 *
 * Returns the keys that disagree. Keys the server doesn't report are skipped —
 * an absent key is unknown, not a mismatch.
 */
export function findCapabilityDrift(
  resolved: unknown,
  want: KalturaCapabilityState,
): string[] {
  if (!resolved || typeof resolved !== "object") return [];
  const live = resolved as Record<string, unknown>;
  const drift: string[] = [];
  for (const [key, field] of CAPABILITY_KEYS) {
    const raw = live[key];
    if (raw === undefined || raw === null) continue;
    const liveOn = raw === true || raw === "on" || raw === 1 || raw === "1";
    const wantOn = field === "knowledge_id" ? want.knowledge_id !== null : want[field] === true;
    if (liveOn !== wantOn) drift.push(key);
  }
  return drift;
}
